import { useState, createContext, useContext, type ReactNode, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

// 表示するトーストの型定義
interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// Contextで共有する値の型定義
interface ToastContextType {
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  reset: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = Date.now() + Math.random();
    setCurrentToast({ id, message, type });
  }, []);

  const showSuccessMessage = (message: string): void => {
    showToast(message, 'success');
  };

  const showErrorMessage = (message: string): void => {
    showToast(message, 'error');
  };

  const reset = (): void => {
    setCurrentToast(null);
  };
  
  const contextValue = {
    showSuccessMessage,
    showErrorMessage,
    reset,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <ToastContainer 
        className="p-3" 
        style={{ 
          zIndex: 9999,
          position: 'fixed',
          top: '20px',
          right: '20px',
          pointerEvents: 'none'
        }}
      >
        {currentToast && (
          <Toast
            key={currentToast.id}
            show={true} 
            onClose={() => setCurrentToast(null)}
            delay={currentToast.type === 'error' ? 8000 : 3000}
            autohide
            className="custom-toast"
            style={{
              pointerEvents: 'auto',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              padding: '0',
              overflow: 'hidden',
              minWidth: '320px',
              maxWidth: '400px'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                gap: '12px',
                position: 'relative'
              }}
            >
              {/* 左側のアクセントバー */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: currentToast.type === 'error' ? '#c9302c' : '#0acf97'
                }}
              />
              
              {/* アイコン */}
              <i
                className={`bi ${currentToast.type === 'error' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'}`}
                style={{
                  fontSize: '20px',
                  color: currentToast.type === 'error' ? '#c9302c' : '#0acf97',
                  flexShrink: 0
                }}
              />
              
              {/* メッセージ */}
              <div
                style={{
                  flex: 1,
                  color: '#4A4A4A',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
              >
                {currentToast.message}
              </div>
              
              {/* 閉じるボタン */}
              <button
                onClick={() => setCurrentToast(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  width: '24px',
                  height: '24px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999';
                }}
                aria-label="閉じる"
              >
                <i className="bi bi-x" style={{ fontSize: '18px' }} />
              </button>
            </div>
          </Toast>
        )}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

